import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, HelpCircle, ChevronDown, CheckCircle2, AlertTriangle,
  Sparkles, ArrowRight, Radio, Users, FileText, Clock, TrendingUp,
  Calendar, MoreHorizontal, Eye, ScanLine, User, Briefcase, BookOpen,
  Gem, BarChart3, Scale, Shield, Landmark, Building2, Gavel, ExternalLink,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/App";
import NotificationBell from "@/components/NotificationBell";
import ImageSlot from "@/components/ImageSlot";
import { TopBar } from "@/components/PageShell";
import PrazosInsights from "@/components/PrazosInsights";
import HermesActivity from "@/components/HermesActivity";
import InsightsJuridicos from "@/components/InsightsJuridicos";

const NAVY = "#0E1D2D";
const GOLD = "#C9953D";
const GOLD_LIGHT = "#D2A85A";
const CREAM = "#F4F2EF";
const BORDER = "#E7E5E2";
const TEXT = "#132238";

const fmtHour = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};
const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return ""; }
};
const firstName = (name) => (name || "").trim().split(/\s+/)[0] || "";
const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Bom dia" : h < 18 ? "Boa tarde" : "Boa noite";
};

/* ===== SUBCOMPONENTS ===== */

// TopBar agora vem de @/components/PageShell — mantido aqui só para compat de testes antigos.

const Hero = ({ user }) => (
  <section className="relative rounded-2xl overflow-hidden border h-[380px]" style={{ borderColor: BORDER }} data-testid="dashboard-hero">
    <ImageSlot
      slot="dashboard_hero"
      fallback="/hermes-background.png"
      alt="Hermes · nascer do sol sobre as montanhas"
      className="absolute inset-0 w-full h-full object-cover"
    />
    {/* Gradiente do lado esquerdo para dar contraste ao texto, preservando o pôr do sol e a asa dourada à direita */}
    <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(14,29,45,0.78) 0%, rgba(14,29,45,0.55) 35%, rgba(14,29,45,0.15) 60%, rgba(14,29,45,0) 100%)" }}/>
    <div className="relative h-full flex flex-col justify-center px-10 max-w-2xl">
      <div className="text-[11px] uppercase tracking-[0.25em] font-sans font-semibold mb-2" style={{ color: GOLD_LIGHT }}>
        HERMES · {new Date().toLocaleDateString("pt-BR", { weekday: "long" })}
      </div>
      <h1 className="font-serif text-6xl leading-[1.02] font-medium tracking-tight text-white" style={{ textShadow: "0 2px 24px rgba(0,0,0,0.35)" }}>
        {greeting()}, <span className="italic">{firstName(user?.name) || "Advogado(a)"}.</span>
      </h1>
      <p className="mt-4 font-serif text-xl text-white/90" style={{ textShadow: "0 1px 12px rgba(0,0,0,0.4)" }}>
        O HERMES já verificou seu escritório hoje.
      </p>
      <p className="mt-5 font-serif italic text-base text-white/85">
        <span className="text-[#E2C275] text-2xl mr-1 leading-none">"</span>
        Disciplina hoje. Liberdade amanhã.
        <span className="text-[#E2C275] text-2xl ml-1 leading-none">"</span>
      </p>
    </div>
  </section>
);

const StatusChip = ({ ok = true, title, sub, alert = false }) => (
  <div className="bg-white rounded-xl border p-3.5 flex items-start gap-3 hover:shadow-sm transition"
       style={{ borderColor: BORDER }}>
    <div className={`w-8 h-8 rounded-full inline-flex items-center justify-center shrink-0 ${
      alert ? "bg-amber-50 text-amber-600" : ok ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"
    }`}>
      {alert ? <AlertTriangle className="w-4 h-4"/> : <CheckCircle2 className="w-4 h-4"/>}
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-[13px] font-semibold text-[#132238] leading-tight">{title}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  </div>
);

const HermesIaChip = () => (
  <Link to="/hermes-ia" className="rounded-xl p-3.5 flex items-center gap-3 group transition hover:shadow-lg"
        style={{ background: `linear-gradient(135deg, ${NAVY}, ${NAVY}ee)`, border: `1px solid ${GOLD}55` }}>
    <div className="w-9 h-9 rounded-full inline-flex items-center justify-center shrink-0"
         style={{ background: `${GOLD}22`, color: GOLD }}>
      <Sparkles className="w-4 h-4"/>
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-[13px] font-semibold text-white leading-tight">HERMES IA</div>
      <div className="text-[11px] text-slate-300 mt-0.5">Seu assistente sempre pronto.</div>
    </div>
    <ArrowRight className="w-4 h-4 text-[#C9953D] group-hover:translate-x-1 transition"/>
  </Link>
);

const KpiCard = ({ icon: I, label, value, color = TEXT }) => (
  <div className="bg-white rounded-xl border p-4" style={{ borderColor: BORDER }}>
    <div className="flex items-center gap-2 mb-2">
      <span className="w-7 h-7 rounded-md inline-flex items-center justify-center" style={{ background: CREAM }}>
        <I className="w-3.5 h-3.5" style={{ color: GOLD }}/>
      </span>
      <span className="text-[11px] text-slate-500 uppercase tracking-wider">{label}</span>
    </div>
    <div className="font-serif text-3xl leading-none font-medium tracking-tight" style={{ color }}>{value ?? "—"}</div>
  </div>
);

const AttentionTable = ({ items }) => {
  if (!items) return <div className="text-sm text-slate-400 p-6 text-center">Carregando…</div>;
  if (items.length === 0) return <div className="text-sm text-slate-400 p-6 text-center">Nenhum item requer sua atenção agora.</div>;
  const badge = (kind) => {
    const map = {
      CRITICO: "bg-rose-50 text-rose-800 border border-rose-200",
      ATENCAO: "bg-amber-50 text-amber-800 border border-amber-200",
      INFO: "bg-blue-50 text-blue-800 border border-blue-200",
    };
    return `text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${map[kind] || map.INFO}`;
  };
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b" style={{ borderColor: BORDER }}>
          <th className="py-2 font-semibold">Prioridade</th>
          <th className="py-2 font-semibold">Prazo</th>
          <th className="py-2 font-semibold">Processo</th>
          <th className="py-2 font-semibold">Cliente</th>
          <th className="py-2 font-semibold">Providência</th>
          <th className="py-2 font-semibold text-right">Ações</th>
        </tr>
      </thead>
      <tbody>
        {items.slice(0, 5).map((it, i) => (
          <tr key={i} className="border-b last:border-0" style={{ borderColor: BORDER }} data-testid={`attention-${i}`}>
            <td className="py-3"><span className={badge(it.kind)}>{it.kind_label}</span></td>
            <td className="py-3 text-[#132238] font-medium">{fmtDate(it.at)}</td>
            <td className="py-3 text-slate-600 font-mono text-[12px]">{it.process}</td>
            <td className="py-3 text-slate-600">{it.client || "—"}</td>
            <td className="py-3 text-slate-600 max-w-[280px] truncate">{it.action}</td>
            <td className="py-3 text-right">
              {it.kind === "CRITICO"
                ? <button className="h-8 px-3 rounded-md text-white text-[12px] font-semibold" style={{ background: NAVY }}>Cumprir com HERMES</button>
                : <button className="h-8 px-3 rounded-md border text-[12px] text-slate-700 hover:bg-slate-50" style={{ borderColor: BORDER }}>Ver detalhes</button>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const TimeCard = ({ minutes = 0, delta = null }) => {
  const h = Math.floor(minutes / 60); const m = minutes % 60;
  const bars = [40, 55, 45, 70, 60, 85];
  return (
    <div className="bg-white rounded-2xl border p-5" style={{ borderColor: BORDER }} data-testid="time-back-card">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Seu tempo voltou</div>
      <div className="font-serif text-3xl text-[#132238] leading-none">{h}h {String(m).padStart(2, "0")}min</div>
      <div className="text-[11px] text-slate-500 mt-1">nos últimos 30 dias</div>
      {delta != null && (
        <div className="mt-2 text-[11px] font-semibold inline-flex items-center gap-1" style={{ color: GOLD }}>
          <TrendingUp className="w-3 h-3"/> +{delta}% vs. mês anterior
        </div>
      )}
      <div className="flex items-end gap-1.5 h-14 mt-4">
        {bars.map((v, i) => (
          <div key={i} className="flex-1 rounded-t" style={{
            background: `linear-gradient(180deg, ${GOLD_LIGHT}, ${GOLD})`, height: `${v}%`,
          }}/>
        ))}
      </div>
      <div className="mt-4 rounded-lg p-3 font-serif italic text-[13px] text-[#132238]/80" style={{ background: CREAM }}>
        <span style={{ color: GOLD }}>"</span>
        Tempo é o seu bem mais valioso. O HERMES cuida do resto.
        <span style={{ color: GOLD }}>"</span>
      </div>
    </div>
  );
};

const AgendaCard = ({ items = [] }) => (
  <div className="bg-white rounded-2xl border p-5" style={{ borderColor: BORDER }} data-testid="agenda-card">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <Calendar className="w-4 h-4" style={{ color: GOLD }}/>
        <div className="text-sm font-semibold text-[#132238]">Agenda de hoje</div>
      </div>
      <Link to="/agenda" className="text-[11px] text-slate-500 hover:text-[#132238]">Ver agenda</Link>
    </div>
    {items.length === 0 ? (
      <div className="text-xs text-slate-400 py-6 text-center">Nenhum compromisso hoje.</div>
    ) : (
      <ul className="space-y-3 relative">
        <div className="absolute left-[7px] top-1 bottom-1 w-px" style={{ background: `${GOLD}44` }}/>
        {items.slice(0, 4).map((it, i) => (
          <li key={i} className="pl-6 relative">
            <span className="absolute left-[3px] top-1.5 w-2 h-2 rounded-full" style={{ background: GOLD }}/>
            <div className="text-[11px] font-semibold text-[#132238]">{it.time}</div>
            <div className="text-[13px] text-slate-700 leading-snug">{it.title}</div>
            {it.hint && <div className="text-[11px] text-slate-400">{it.hint}</div>}
          </li>
        ))}
      </ul>
    )}
  </div>
);

const InstitutionalCard = () => (
  <div className="relative rounded-2xl border overflow-hidden p-6 text-center" style={{ borderColor: `${GOLD}55`, background: `linear-gradient(180deg, #FFFFFF, ${CREAM})` }}>
    <div className="text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: GOLD }}>Hermes</div>
    <p className="font-serif italic text-[15px] text-[#132238] leading-snug">
      Grandes resultados nascem de<br/>rotinas bem resolvidas.
    </p>
    <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-500">
      hermes<span style={{ color: GOLD }}>·</span>advocacia
    </div>
  </div>
);

/* ===== Módulos do Dashboard 360 ===== */

// Faixa DJEN em destaque (gradiente vermelho + contador de publicações 24h)
const DjenHighlight = ({ health = {}, unread = 0, onSync, syncing }) => {
  const status = health?.last_success_at ? `Última sincronização: ${fmtHour(health.last_success_at)}` : "Aguardando primeira sincronização";
  return (
    <Link to="/djen" className="group relative rounded-2xl overflow-hidden border block" style={{ borderColor: "#7f1d1d" }} data-testid="djen-highlight">
      <div className="p-5 flex items-center gap-5 text-white" style={{ background: "linear-gradient(120deg, #b91c1c 0%, #7f1d1d 65%, #4c1d1d 100%)" }}>
        <div className="w-14 h-14 rounded-full bg-white/15 inline-flex items-center justify-center shrink-0 ring-1 ring-white/25">
          <Radio className="w-6 h-6"/>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-[0.25em] text-white/70">DJEN · CNJ</div>
          <div className="font-serif text-2xl leading-tight">Consultar publicações de hoje</div>
          <div className="text-[12px] text-white/80 mt-1 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-300"/> {status}</span>
            <span>·</span>
            <span><strong>{health?.last_24h_count ?? 0}</strong> nas últimas 24h</span>
            {onSync && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSync(); }}
                disabled={syncing}
                data-testid="djen-sync-now"
                className="ml-2 h-7 px-3 rounded-full bg-white text-rose-800 text-[11px] font-bold uppercase tracking-wider disabled:opacity-60"
              >
                {syncing ? "Sincronizando…" : "Sincronizar agora"}
              </button>
            )}
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-white text-rose-800 font-serif text-3xl inline-flex items-center justify-center shadow-lg">{unread}</div>
        </div>
      </div>
    </Link>
  );
};

// Faixa Radar de Prazos (visual claro, alerta amarelo/vermelho)
const PrazosStrip = ({ overdue = 0, week = 0 }) => (
  <Link to="/prazos" className="rounded-2xl border bg-white p-4 flex items-center gap-4 hover:shadow-sm transition" style={{ borderColor: BORDER }} data-testid="prazos-strip">
    <div className="w-10 h-10 rounded-full bg-rose-50 text-rose-600 inline-flex items-center justify-center shrink-0"><AlertTriangle className="w-5 h-5"/></div>
    <div className="flex-1 min-w-0">
      <div className="text-[11px] uppercase tracking-[0.22em] text-rose-700 font-semibold">Radar de Prazos</div>
      <div className="text-[15px] text-[#132238]">Acompanhe seus prazos processuais</div>
    </div>
    <div className="flex items-center gap-2">
      {overdue > 0 && <span className="text-[11px] font-bold text-white bg-slate-900 px-2.5 py-1 rounded-full">{overdue} vencido{overdue>1?"s":""}</span>}
      {week > 0 && <span className="text-[11px] font-bold text-rose-700 bg-rose-100 px-2.5 py-1 rounded-full">{week} nesta semana</span>}
      <ArrowRight className="w-4 h-4 text-slate-400"/>
    </div>
  </Link>
);

// Cartão de módulo padronizado (ícone + eyebrow + título + descrição + Acessar →)
const ModuleTile = ({ to, icon: I, eyebrow, iconTint = GOLD, title, description, cta = "Acessar", tone = "light", dot, dotColor }) => {
  const isDark = tone === "dark";
  const iconBg = isDark ? "rgba(255,255,255,0.08)" : `${GOLD}18`;
  return (
    <Link to={to} className={`group relative rounded-2xl border p-5 block transition ${isDark ? "" : "bg-white hover:shadow-sm"}`}
          style={isDark
            ? { background: `linear-gradient(140deg, ${NAVY} 0%, #172a3f 100%)`, borderColor: "#1c3552" }
            : { borderColor: BORDER }}
          data-testid={`module-${to.replace(/\//g,'-')}`}>
      <div className="flex items-center gap-2">
        {I && (
          <span className="w-8 h-8 rounded-md inline-flex items-center justify-center" style={{ background: iconBg }}>
            <I className="w-4 h-4" style={{ color: isDark ? GOLD_LIGHT : iconTint }}/>
          </span>
        )}
        {dot && <span className="w-2 h-2 rounded-full" style={{ background: dotColor || GOLD }}/>}
        <span className={`text-[10px] uppercase tracking-[0.22em] font-semibold ${isDark ? "text-[#D2A85A]" : "text-slate-500"}`}>{eyebrow}</span>
      </div>
      <div className={`mt-3 font-serif text-xl leading-tight ${isDark ? "text-white" : "text-[#132238]"}`}>{title}</div>
      <div className={`mt-1.5 text-[13px] leading-snug ${isDark ? "text-slate-300" : "text-slate-600"}`}>{description}</div>
      <div className={`mt-4 text-[13px] font-semibold inline-flex items-center gap-1 ${isDark ? "text-[#D2A85A]" : ""}`}
            style={!isDark ? { color: GOLD } : undefined}>
        {cta} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition"/>
      </div>
    </Link>
  );
};

// Faixa Hermes Mineral · Créditos
const MineralStrip = ({ credits = 0 }) => (
  <div className="rounded-2xl border p-5 flex items-center gap-4" style={{ borderColor: `${GOLD}66`, background: "linear-gradient(90deg, #FFF6D6 0%, #FBE7A6 100%)" }} data-testid="mineral-strip">
    <div className="w-11 h-11 rounded-lg bg-[#0E1D2D] text-[#D2A85A] inline-flex items-center justify-center shrink-0"><Gem className="w-5 h-5"/></div>
    <div className="flex-1 min-w-0">
      <div className="text-[10px] uppercase tracking-[0.22em] text-slate-700 font-semibold">Hermes Mineral · Créditos</div>
      <div className="font-serif text-xl text-[#132238]">{credits} consulta{credits === 1 ? "" : "s"} disponíve{credits === 1 ? "l" : "is"}</div>
      <div className="text-[12px] text-slate-700">Use um crédito para consultar um processo mineral sem pagamento adicional.</div>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <Link to="/mineral" className="h-9 px-4 rounded-full bg-[#F5D785] hover:bg-[#EFCC6E] text-[#0E1D2D] text-[12px] font-bold inline-flex items-center gap-1">Nova consulta grátis <ArrowRight className="w-3.5 h-3.5"/></Link>
      <Link to="/mineral" className="h-9 px-3 rounded-full bg-white border text-[#0E1D2D] text-[12px] font-semibold" style={{ borderColor: BORDER }}>Extrato</Link>
      <Link to="/mineral" className="h-9 px-3 rounded-full bg-white border text-[#0E1D2D] text-[12px] font-semibold" style={{ borderColor: BORDER }}>Radar</Link>
    </div>
  </div>
);

// Banner "Advocacia sem Fronteiras" (com placeholder de imagem)
const FrontierBanner = () => (
  <div className="relative rounded-2xl overflow-hidden border h-[190px]" style={{ borderColor: "#1c3552" }} data-testid="frontier-banner">
    <ImageSlot slot="advocacia_sem_fronteiras" fallback="/hermes-banner-hero.jpg" alt="Advocacia sem Fronteiras" className="absolute inset-0 w-full h-full object-cover"/>
    <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(14,29,45,0.92) 0%, rgba(14,29,45,0.55) 45%, rgba(14,29,45,0.15) 80%, rgba(14,29,45,0) 100%)" }}/>
    <div className="relative h-full flex items-center px-8 gap-6">
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: GOLD_LIGHT }}>Advocacia sem Fronteiras</div>
        <div className="font-serif text-3xl text-white mt-2 leading-tight">Hermes trabalha enquanto você advoga</div>
        <div className="text-[13px] text-white/80 font-serif italic mt-1">"Mais tempo para o que realmente importa."</div>
      </div>
      <div className="hidden md:block shrink-0 text-right">
        <div className="text-[11px] text-white/80 font-serif italic">"Disciplina hoje. Liberdade sempre."</div>
        <button className="mt-3 h-9 px-5 rounded-full text-[12px] font-bold" style={{ background: "#F5D785", color: NAVY }}>Explorar todos os recursos</button>
      </div>
    </div>
  </div>
);

/* ===== MAIN ===== */

export default function Dashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState({});
  const [kpi, setKpi] = useState({});
  const [pubs, setPubs] = useState([]);
  const [docs, setDocs] = useState([]);
  const [attention, setAttention] = useState([]);
  const [djenHealth, setDjenHealth] = useState({});
  const [mineralCredits, setMineralCredits] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    api.get("/profile").then(r => setProfile(r.data || {})).catch(() => {});
    api.get("/djen/dashboard-health").then(r => setDjenHealth(r.data || {})).catch(() => {});
    api.get("/djen/recent?hours=24&limit=5").then(r => setPubs(r.data?.items || [])).catch(() => setPubs([]));
    api.get("/dashboard/kpis").then(r => setKpi(r.data || {})).catch(() => setKpi({}));
    api.get("/dashboard/attention").then(r => setAttention(r.data?.items || [])).catch(() => setAttention([]));
    api.get("/dashboard/documents-recent").then(r => setDocs(r.data?.items || [])).catch(() => setDocs([]));
    api.get("/mineral/credits/summary").then(r => setMineralCredits(r.data?.available ?? r.data?.credits ?? 0)).catch(() => {});
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.post("/djen/sync-now");
      toast.success("Sincronização DJEN iniciada");
      const r = await api.get("/djen/dashboard-health");
      setDjenHealth(r.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="flex-1 min-w-0 flex flex-col">
      <TopBar/>
      <main className="flex-1 p-3 sm:p-6 lg:p-8 font-sans" style={{ color: TEXT }}>
        {/* HERO */}
        <Hero user={user}/>

        {/* DJEN destaque + Radar de Prazos (visão executiva imediata) */}
        <div className="mt-6 space-y-3" data-testid="module-strips">
          <DjenHighlight health={djenHealth} unread={djenHealth?.unread_count ?? 0} onSync={handleSync} syncing={syncing}/>
          <PrazosStrip overdue={kpi?.prazos_vencidos || 0} week={kpi?.prazos_semana || 0}/>
        </div>

        {/* MEU DIA · ATENÇÃO · HERMES RECOMENDA */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="module-grid-day">
          <ModuleTile
            to="/preparado"
            icon={Sparkles}
            eyebrow="MEU DIA"
            title="Agenda operacional"
            description="Prazos, publicações e tarefas em destaque para hoje."
            cta="Abrir agenda"
          />
          <ModuleTile
            to="/prazos"
            icon={AlertTriangle}
            iconTint="#E11D48"
            eyebrow="ATENÇÃO"
            title="Pontos críticos"
            description="Riscos, alertas e pendências que precisam de decisão sua."
            cta="Ver radar"
          />
          <ModuleTile
            to="/inteligencia"
            icon={Sparkles}
            eyebrow="HERMES RECOMENDA"
            title="Insights inteligentes"
            description="Sugestões automáticas baseadas nos seus casos e clientes."
            cta="Explorar"
            tone="dark"
          />
        </div>

        {/* INTELIGÊNCIA 360 — três subcategorias */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="module-grid-intel">
          <ModuleTile
            to="/inteligencia?product=radar-360"
            eyebrow="INTELIGÊNCIA 360"
            dot dotColor="#E11D48"
            title="Radar de Risco 360"
            description="RFB + CGU + PGFN + TCU + PNCP em uma timeline unificada."
            cta="Acessar"
          />
          <ModuleTile
            to="/inteligencia?product=contratacoes"
            eyebrow="INTELIGÊNCIA 360"
            dot dotColor={NAVY}
            title="Contratações Públicas"
            description="PNCP · TCU · CVM · CNO por CNPJ."
            cta="Acessar"
          />
          <ModuleTile
            to="/inteligencia?product=camara-senado"
            eyebrow="INTELIGÊNCIA 360"
            dot dotColor={GOLD}
            title="Câmara & Senado"
            description="Proposições legislativas por tema, ano, autor."
            cta="Acessar"
          />
        </div>

        {/* Hermes Mineral · Créditos */}
        <div className="mt-4">
          <MineralStrip credits={mineralCredits}/>
        </div>

        {/* Ferramentas primárias: Ler Documentos + Meus Clientes + Meu Perfil */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="module-grid-tools">
          <ModuleTile
            to="/reader"
            icon={ScanLine}
            eyebrow="OCR · DOCUMENTOS"
            title="Ler Documentos"
            description="RG, CNH ou comprovante — o OCR faz o resto."
            cta="Abrir leitor"
            tone="dark"
          />
          <ModuleTile
            to="/clients"
            icon={Users}
            eyebrow="ESCRITÓRIO"
            title="Meus Clientes"
            description="Pesquise, edite ou gere um documento."
            cta="Ver clientes"
          />
          <ModuleTile
            to="/profile"
            icon={User}
            eyebrow="IDENTIDADE"
            title="Meu Perfil"
            description="OAB, escritório e endereço profissional."
            cta="Abrir perfil"
          />
        </div>

        {/* Extras (Jurisprudência · Vale a Pena · Cálculos · Modelos · Financeiro · Kanban) */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="module-grid-extra">
          <ModuleTile to="/jurisprudencia" icon={BookOpen} eyebrow="BASE" title="Jurisprudência" description="Busca nacional STJ/TRF/TJ." cta="Buscar"/>
          <ModuleTile to="/vale-a-pena" icon={BarChart3} eyebrow="ESTRATÉGIA" title="Vale a Pena?" description="Simule chance de êxito por tema." cta="Simular"/>
          <ModuleTile to="/calculos" icon={Scale} eyebrow="LIQUIDAÇÃO" title="Cálculos" description="Atualização e liquidação." cta="Abrir"/>
          <ModuleTile to="/templates" icon={FileText} eyebrow="DOCS" title="Modelos & Cláusulas" description="Biblioteca do escritório." cta="Ver modelos"/>
          <ModuleTile to="/finance" icon={TrendingUp} eyebrow="FINANCEIRO" title="Fluxo & DRE" description="Receita, despesas e honorários." cta="Ver painel"/>
          <ModuleTile to="/kanban" icon={Briefcase} eyebrow="ESCRITÓRIO" title="Kanban" description="Fluxo operacional do dia a dia." cta="Ver quadro"/>
        </div>

        {/* STATUS ROW */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <StatusChip ok title="DJEN consultado" sub={djenHealth?.last_success_at ? `Última: ${fmtHour(djenHealth.last_success_at)}` : "Aguardando"}/>
          <StatusChip ok title={`${kpi?.processos_ativos ?? "—"} processos`} sub="monitorados"/>
          <StatusChip ok title="Sistemas integrados" sub="em funcionamento"/>
          <StatusChip alert title={`${djenHealth?.unread_count ?? 0} itens`} sub="precisam da sua atenção"/>
          <HermesIaChip/>
        </div>

        {/* PANORAMA HERMES · Radar + Atividade + Insights */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="panorama-hermes">
          <PrazosInsights compact/>
          <HermesActivity limit={10}/>
          <InsightsJuridicos/>
        </div>

        {/* MAIN GRID: KPI + attention + pubs/docs on left · time/agenda/institutional on right */}
        <div className="mt-6 grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
          <div className="space-y-6 min-w-0">
            {/* KPI CARDS */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <KpiCard icon={FileText} label="Processos ativos" value={kpi?.processos_ativos}/>
              <KpiCard icon={Users} label="Clientes" value={kpi?.clientes}/>
              <KpiCard icon={Clock} label="Prazos esta semana" value={kpi?.prazos_semana} color={kpi?.prazos_semana > 0 ? "#991B1B" : TEXT}/>
              <KpiCard icon={Radio} label="Publicações novas" value={djenHealth?.last_24h_count ?? 0}/>
              <KpiCard icon={TrendingUp} label="Tempo devolvido" value={kpi?.tempo_devolvido_str || "—"}/>
            </div>

            {/* ATTENTION CARD */}
            <div className="bg-white rounded-2xl border p-5" style={{ borderColor: BORDER }} data-testid="attention-card">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-rose-600"/>
                    <h2 className="font-serif text-lg text-[#132238]">Sua atenção hoje</h2>
                    {attention.length > 0 && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700">{attention.length}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">Itens que precisam da sua ação</div>
                </div>
                <Link to="/prazos" className="text-[11px] text-slate-500 hover:text-[#132238]">Ver todos</Link>
              </div>
              <AttentionTable items={attention}/>
            </div>

            {/* LOWER GRID: publications + documents */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl border p-5" style={{ borderColor: BORDER }} data-testid="publications-card">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Radio className="w-4 h-4" style={{ color: GOLD }}/>
                      <h2 className="font-serif text-base text-[#132238]">Últimas publicações</h2>
                      {pubs.length > 0 && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700">{pubs.length}</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">Encontradas nas últimas 24h</div>
                  </div>
                  <Link to="/djen" className="text-[11px] text-slate-500 hover:text-[#132238]">Ver todas</Link>
                </div>
                {pubs.length === 0 ? (
                  <div className="text-xs text-slate-400 py-8 text-center">Sem publicações no período.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b" style={{ borderColor: BORDER }}>
                        <th className="py-2">Data</th><th className="py-2">Processo</th><th className="py-2">Descrição</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pubs.slice(0, 4).map((p, i) => (
                        <tr key={i} className="border-b last:border-0" style={{ borderColor: BORDER }}>
                          <td className="py-2 text-[12px] text-slate-500">{fmtDate(p.data_publicacao)}</td>
                          <td className="py-2 text-[11px] text-slate-600 font-mono">{p.numero_processo}</td>
                          <td className="py-2 text-[12px] text-[#132238] truncate max-w-[220px]">{p.preview}</td>
                          <td className="py-2 text-right"><Link to={p.link || "/djen"} className="text-[11px] text-[#132238] hover:underline inline-flex items-center gap-1"><Eye className="w-3 h-3"/> Ver</Link></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="bg-white rounded-2xl border p-5" style={{ borderColor: BORDER }} data-testid="documents-card">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" style={{ color: GOLD }}/>
                      <h2 className="font-serif text-base text-[#132238]">Documentos recentes</h2>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">Adicionados ou processados</div>
                  </div>
                  <Link to="/documentos" className="text-[11px] text-slate-500 hover:text-[#132238]">Ver todos</Link>
                </div>
                {docs.length === 0 ? (
                  <div className="text-xs text-slate-400 py-8 text-center">Sem documentos recentes.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b" style={{ borderColor: BORDER }}>
                        <th className="py-2">Nome</th><th className="py-2">Origem</th><th className="py-2">Data</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {docs.slice(0, 4).map((d, i) => (
                        <tr key={i} className="border-b last:border-0" style={{ borderColor: BORDER }}>
                          <td className="py-2 text-[12px] text-[#132238] truncate max-w-[180px]">{d.name}</td>
                          <td className="py-2"><span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{d.source || "Upload"}</span></td>
                          <td className="py-2 text-[12px] text-slate-500">{fmtDate(d.created_at)}</td>
                          <td className="py-2 text-right"><Link to={d.link || "/documentos"} className="text-[11px] text-[#132238] hover:underline">Abrir</Link></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <aside className="space-y-6">
            <TimeCard minutes={kpi?.tempo_devolvido_min || 0} delta={kpi?.tempo_delta_pct}/>
            <AgendaCard items={kpi?.agenda_hoje || []}/>
          </aside>
        </div>

        {/* INSTITUTIONAL ROW · alinhado com documentos como na referência */}
        <div className="mt-6 grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
          <div/>
          <InstitutionalCard/>
        </div>

        {/* ADVOCACIA SEM FRONTEIRAS · encerramento institucional */}
        <div className="mt-6">
          <FrontierBanner/>
        </div>
      </main>
    </div>
  );
}
